import React, { useContext, useEffect, useMemo, useState } from "react";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { featureCollection } from "@turf/helpers";
import { Spinner } from "react-bootstrap";
import type { GeoJSONSource } from "maplibre-gl";
import { searchSingleLayerForConflicts } from "src/components/ConflictSearchCalculations";
import { KeyValueLine, KeyValuesHorizontal } from "src/propertyInfoTemplates/PropertyComponents";
import { MapitStateContext } from "src/states/MapitState";
import { addDisplayLayer, removeDisplayLayer } from "src/utils/mapLayerHelpers";
import {
   FavoriteConflictSearchLayer,
   formatUserFilterNote,
   loadFavoriteConflictGroups,
   mapFavoriteGroupsToSearchLayers
} from "./favoriteConflictGroups";

const DEFAULT_PREVIEW_SOURCE_ID = "favorite-conflict-results";

export type FavoriteConflictSearchPresentation = "panel" | "list";

export type FavoriteConflictSearchProps = {
   geometry?: any;
   presentation?: FavoriteConflictSearchPresentation;
   previewSourceId?: string;
};

type FavoriteConflictResult = FavoriteConflictSearchLayer & {
   conflict: any | null;
   err: string;
   matchedCount: number;
   progress: number;
};

function toSearchFeatureCollection(geometry: any): any | null {
   if (!geometry) return null;
   if (geometry.type === "FeatureCollection") return geometry;
   if (geometry.type === "Feature") return featureCollection([geometry]);
   if (geometry.type) {
      return featureCollection([{
         type: "Feature",
         geometry,
         properties: {}
      } as any]);
   }
   return null;
}

function matchedCountFromFeatureCollection(value: any): number {
   const metadataCount = parseInt(value?.metadata?.numberMatched ?? "", 10);
   if (Number.isFinite(metadataCount)) return metadataCount;
   return Array.isArray(value?.features) ? value.features.length : 0;
}

function initialResults(layers: FavoriteConflictSearchLayer[]): FavoriteConflictResult[] {
   return layers.map((layer) => ({
      ...layer,
      conflict: null,
      err: "",
      matchedCount: -1,
      progress: 0
   }));
}

async function searchLayer(
   searchGeometry: any,
   layer: FavoriteConflictSearchLayer
): Promise<FavoriteConflictResult> {
   const rawResult = await searchSingleLayerForConflicts(
      searchGeometry,
      layer.source,
      layer.layer,
      { filter: layer.filter }
   );
   return {
      ...layer,
      conflict: rawResult,
      err: "",
      matchedCount: matchedCountFromFeatureCollection(rawResult),
      progress: 1
   };
}

function FavoriteConflictResultList(props: {
   results: FavoriteConflictResult[];
   selectedResultId: string | null;
   onTogglePreview: (result: FavoriteConflictResult) => void;
}) {
   const groupedResults = useMemo(() => {
      const groups = new Map<string, FavoriteConflictResult[]>();
      for (const result of props.results) {
         const title = result.groupTitle || Localization.getText("Favorite layers");
         const rows = groups.get(title) ?? [];
         rows.push(result);
         groups.set(title, rows);
      }
      return [...groups.entries()];
   }, [props.results]);

   return (
      <div className="ConflictSearchScrollContainer favorite-conflict-search__scroll">
         <div className="favorite-conflict-search__groups">
            {groupedResults.map(([groupTitle, rows]) => (
               <div key={groupTitle} className="ConflictSourceGroup">
                  <div className="ConflictSourceGroupHeader">
                     <div className="ConflictSourceGroupTitle"><b>{groupTitle}</b></div>
                     <span>{Localization.getText("Conflicts")}</span>
                  </div>
                  <div className="ConflictSourceGroupContent">
                     {rows.map((result) => {
                        const selected = props.selectedResultId === result.id;
                        const filterNote = formatUserFilterNote(result.filter);
                        return (
                           <div
                              key={result.id}
                              className="ConflictItem"
                              onClick={() => props.onTogglePreview(result)}
                           >
                              <div>
                                 <div className={selected ? "ConflictItemSelected" : ""}>
                                    {result.title}
                                 </div>
                                 {filterNote && (
                                    <div className="ConflictItemSubnote">{filterNote}</div>
                                 )}
                              </div>
                              {result.err
                                 ? <div className="ConflictItemError">{Localization.getText("Error")}</div>
                                 : (
                                    <div className="ConflictItemDetails">
                                       {!result.progress
                                          ? <Spinner animation="border" size="sm" />
                                          : result.matchedCount}
                                    </div>
                                 )}
                           </div>
                        );
                     })}
                  </div>
               </div>
            ))}
         </div>
      </div>
   );
}

export function FavoriteConflictSearch(props: FavoriteConflictSearchProps) {
   const presentation = props.presentation ?? "panel";
   const previewSourceId = props.previewSourceId || DEFAULT_PREVIEW_SOURCE_ID;
   const { state } = useContext(SessionContext);
   const { state: mapitState } = useContext(MapitStateContext);
   const [searchLayers, setSearchLayers] = useState<FavoriteConflictSearchLayer[] | null>(null);
   const [hasMarkedGroups, setHasMarkedGroups] = useState(false);
   const [results, setResults] = useState<FavoriteConflictResult[]>([]);
   const [selectedResultId, setSelectedResultId] = useState<string | null>(null);

   const geometryKey = useMemo(() => {
      if (!props.geometry) return "";
      try {
         return JSON.stringify(props.geometry);
      } catch {
         return `${Date.now()}`;
      }
   }, [props.geometry]);

   const searchGeometry = useMemo(
      () => toSearchFeatureCollection(props.geometry),
      [geometryKey, props.geometry]
   );

   useEffect(() => {
      let cancelled = false;
      loadFavoriteConflictGroups(state.customerRef, state.userRef)
         .then((groups) => {
            if (cancelled) return;
            const markedGroups = groups.filter((group) => group.favoriteConflictSearch);
            setHasMarkedGroups(markedGroups.length > 0);
            setSearchLayers(mapFavoriteGroupsToSearchLayers(markedGroups));
         })
         .catch((error) => {
            console.error("Favorite conflict search failed to load groups", error);
            if (!cancelled) {
               setHasMarkedGroups(false);
               setSearchLayers([]);
            }
         });
      return () => {
         cancelled = true;
      };
   }, [state.customerRef, state.userRef]);

   useEffect(() => {
      const map = mapitState.map?.getMapPop();
      if (!map) return;
      if (!map.getSource(previewSourceId)) {
         addDisplayLayer(map, previewSourceId);
      }
      return () => {
         try {
            if (map.getSource(previewSourceId)) {
               removeDisplayLayer(map, previewSourceId);
            }
         } catch (error) {
            console.debug("Favorite conflict search cleanup failed", error);
         }
      };
   }, [mapitState.map, previewSourceId]);

   useEffect(() => {
      const map = mapitState.map?.getMapPop();
      const source = map?.getSource(previewSourceId) as GeoJSONSource | undefined;
      if (source) {
         source.setData(featureCollection([]) as any);
      }
      setSelectedResultId(null);
   }, [geometryKey, mapitState.map, previewSourceId]);

   useEffect(() => {
      let cancelled = false;

      if (!searchGeometry || !searchLayers?.length) {
         setResults([]);
         return () => {
            cancelled = true;
         };
      }

      setResults(initialResults(searchLayers));

      const groupedBySource = searchLayers.reduce<Record<string, FavoriteConflictSearchLayer[]>>((groups, layer) => {
         groups[layer.source] ??= [];
         groups[layer.source].push(layer);
         return groups;
      }, {});

      Promise.all(
         Object.values(groupedBySource).map(async (layers) => {
            for (const layer of layers) {
               let nextResult: FavoriteConflictResult;
               try {
                  nextResult = await searchLayer(searchGeometry, layer);
               } catch (error) {
                  nextResult = {
                     ...layer,
                     conflict: null,
                     err: error instanceof Error ? error.message : String(error),
                     matchedCount: -1,
                     progress: 1
                  };
               }
               if (!cancelled) {
                  setResults((current) =>
                     current.map((result) => result.id === nextResult.id ? nextResult : result)
                  );
               }
            }
         })
      ).catch((error) => {
         console.error("Favorite conflict search failed", error);
      });

      return () => {
         cancelled = true;
      };
   }, [searchGeometry, searchLayers]);

   function previewResult(result: FavoriteConflictResult) {
      const map = mapitState.map?.getMapPop();
      const source = map?.getSource(previewSourceId) as GeoJSONSource | undefined;
      if (!source || result.conflict == null) return;

      if (selectedResultId === result.id) {
         source.setData(featureCollection([]) as any);
         setSelectedResultId(null);
         return;
      }

      source.setData(result.conflict);
      setSelectedResultId(result.id);
   }

   const totalMatches = results.reduce((sum, result) => sum + Math.max(0, result.matchedCount), 0);
   const searchesCompleted = results.reduce((sum, result) => sum + result.progress, 0);
   const isSearching = !!searchGeometry && searchLayers !== null && searchLayers.length > 0 && searchesCompleted < results.length;
   const failedSearches = results.filter((result) => result.err).length;
   const sourcesCount = new Set(results.map((result) => result.source)).size;

   let body: React.ReactNode;
   if (!searchGeometry) {
      body = (
         <div className="favorite-conflict-search__empty">
            {Localization.getText("Pending geometry")}
         </div>
      );
   } else if (searchLayers === null) {
      body = (
         <div className="favorite-conflict-search__empty">
            <Spinner animation="border" size="sm" />
            <span>{Localization.getText("Searching...")}</span>
         </div>
      );
   } else if (!hasMarkedGroups) {
      body = (
         <div className="favorite-conflict-search__empty">
            {Localization.getText("No favorite layers marked for conflict search")}
         </div>
      );
   } else if (searchLayers.length === 0) {
      body = (
         <div className="favorite-conflict-search__empty">
            {Localization.getText("No searchable layers in favorite conflict search")}
         </div>
      );
   } else {
      body = (
         <div className="favorite-conflict-search__content">
            <div className="favorite-conflict-search__toolbar" aria-live="polite">
               <KeyValuesHorizontal>
                  <KeyValueLine valKey={Localization.getText("Total searches")} value={searchesCompleted} />
                  <KeyValueLine valKey={Localization.getText("Conflicts found")} value={totalMatches} />
                  <KeyValueLine valKey={Localization.getText("Searches failed")} value={failedSearches} />
                  <KeyValueLine valKey={Localization.getText("Sources")} value={sourcesCount} />
               </KeyValuesHorizontal>
            </div>
            <FavoriteConflictResultList
               results={results}
               selectedResultId={selectedResultId}
               onTogglePreview={previewResult}
            />
            {!isSearching && totalMatches === 0 && failedSearches === 0 && (
               <div className="favorite-conflict-search__empty">
                  {Localization.getText("No conflicts found for this property")}
               </div>
            )}
         </div>
      );
   }

   const status = !searchGeometry
      ? Localization.getText("Pending geometry")
      : searchLayers === null || isSearching
         ? Localization.getText("Searching...")
         : Localization.getFormattedText("{count} findings", { count: totalMatches });
   const conflictSearchState = !!searchGeometry && (searchLayers === null || isSearching)
      ? "searching"
      : "ready";

   if (presentation === "list") {
      return (
         <div className="favorite-conflict-search favorite-conflict-search--list" data-jpb-conflict-search={conflictSearchState}>
            {body}
         </div>
      );
   }

   return (
      <div className="favorite-conflict-search favorite-conflict-search--panel jpb-mini-conflict-search" data-jpb-conflict-search={conflictSearchState}>
         <details className="jpb-analyzer-details jpb-analyzer-details--flush">
            <summary>
               <div className="jpb-mini-conflict-title">
                  <span>{Localization.getText("Favorite conflict search")}</span>
                  <span className="jpb-mini-conflict-title__status">{status}</span>
               </div>
            </summary>
            <div className="jpb-mini-conflict-content">
               {body}
            </div>
         </details>
      </div>
   );
}
